/**
 * SAFELIS THEME JAVASCRIPT CORE
 * BFCache Revalidation, AJAX Cart, Steppers, Accordions, Filter Tabs
 */

(function() {
  'use strict';

  // --------------------------------------------------------------------------
  // 1. STATE & UTILS
  // --------------------------------------------------------------------------
  const formatMoney = (cents) => {
    const rupees = (cents / 100).toLocaleString('en-IN', {
      maximumFractionDigits: 0
    });
    return `₹${rupees}`;
  };

  const debounce = (func, delay = 300) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), delay);
    };
  };

  // --------------------------------------------------------------------------
  // 2. CART DRAWER & AJAX REVALIDATION
  // --------------------------------------------------------------------------
  const CartManager = {
    drawer: null,
    isOpen: false,
    shippingThreshold: 199900, // ₹1,999 in cents

    init() {
      this.drawer = document.getElementById('CartDrawer');
      if (!this.drawer) return;

      this.bindEvents();
      this.syncCartWithServer();
    },

    bindEvents() {
      // Trigger Drawer Open
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-cart-drawer-trigger]');
        if (trigger) {
          e.preventDefault();
          this.open();
        }

        const closeBtn = e.target.closest('[data-cart-drawer-close]');
        if (closeBtn) {
          e.preventDefault();
          this.close();
        }

        // Stepper buttons
        const stepperBtn = e.target.closest('[data-qty-change]');
        if (stepperBtn) {
          const delta = parseInt(stepperBtn.dataset.qtyChange, 10);
          const stepper = stepperBtn.closest('[data-item-key]');
          const key = stepper.dataset.itemKey;
          const valEl = stepper.querySelector('[data-qty-val]');
          let currentQty = parseInt(valEl.textContent, 10);
          let newQty = currentQty + delta;
          if (newQty < 0) newQty = 0;

          // Optimistic UI
          valEl.textContent = newQty;
          this.debouncedUpdate(key, newQty);
        }

        // Remove item
        const removeBtn = e.target.closest('[data-remove-item]');
        if (removeBtn) {
          const key = removeBtn.dataset.removeItem;
          this.changeQuantity(key, 0);
        }
      });

      // Intercept Add-to-Cart Forms
      document.addEventListener('submit', (e) => {
        const form = e.target.closest('[data-product-form]');
        if (form) {
          e.preventDefault();
          this.addToCart(form);
        }
      });

      // BFCache / Navigation sync listeners
      window.addEventListener('pageshow', () => this.syncCartWithServer());
      window.addEventListener('focus', () => this.syncCartWithServer());
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) this.syncCartWithServer();
      });
    },

    open() {
      this.drawer.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      this.isOpen = true;
    },

    close() {
      this.drawer.classList.remove('is-open');
      document.body.style.overflow = '';
      this.isOpen = false;
    },

    async addToCart(form) {
      const submitBtn = form.querySelector('[data-add-to-cart]');
      if (submitBtn) submitBtn.disabled = true;

      try {
        const formData = new FormData(form);
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData
        });

        if (res.ok) {
          await this.syncCartWithServer();
          this.open();
        }
      } catch (err) {
        console.error('Add to cart error:', err);
      } finally {
        if (submitBtn) submitBtn.disabled = false;
      }
    },

    debouncedUpdate: debounce(function(key, quantity) {
      CartManager.changeQuantity(key, quantity);
    }, 300),

    async changeQuantity(key, quantity) {
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity })
        });
        const cart = await res.json();
        this.renderCart(cart);
      } catch (err) {
        console.error('Change quantity error:', err);
      }
    },

    async syncCartWithServer() {
      try {
        const res = await fetch(`/cart.js?_=${Date.now()}`, {
          cache: 'no-store'
        });
        if (res.ok) {
          const cart = await res.json();
          this.renderCart(cart);
        }
      } catch (err) {
        console.error('Cart sync error:', err);
      }
    },

    renderCart(cart) {
      // Update counters
      document.querySelectorAll('[data-cart-count]').forEach(el => {
        if (el.classList.contains('site-header__cart-badge')) {
          if (cart.item_count > 0) {
            el.textContent = cart.item_count;
            el.classList.remove('is-hidden');
            el.style.display = 'flex';
          } else {
            el.textContent = '';
            el.classList.add('is-hidden');
            el.style.display = 'none';
          }
        } else {
          el.textContent = cart.item_count;
        }
      });

      // Update Subtotal
      const subtotalEl = this.drawer.querySelector('[data-cart-subtotal]');
      if (subtotalEl) {
        subtotalEl.textContent = formatMoney(cart.total_price);
      }

      // Update Shipping Bar
      const shippingMessage = this.drawer.querySelector('[data-shipping-message]');
      const shippingFill = this.drawer.querySelector('.cart-drawer__progress-fill');
      if (shippingMessage && shippingFill) {
        if (cart.total_price >= this.shippingThreshold) {
          shippingMessage.innerHTML = '🎉 You’ve unlocked <strong>Free Express Shipping</strong>!';
          shippingFill.style.width = '100%';
        } else {
          const remaining = this.shippingThreshold - cart.total_price;
          shippingMessage.innerHTML = `Add <strong>${formatMoney(remaining)}</strong> more to get Free Express Shipping!`;
          const progress = Math.min((cart.total_price / this.shippingThreshold) * 100, 100);
          shippingFill.style.width = `${progress}%`;
        }
      }

      // Render line items
      const itemsContainer = this.drawer.querySelector('[data-cart-drawer-items]');
      const footer = this.drawer.querySelector('[data-cart-drawer-footer]');

      if (!itemsContainer) return;

      if (cart.item_count === 0) {
        if (footer) footer.classList.add('hidden');
        itemsContainer.innerHTML = `
          <div class="cart-drawer__empty">
            <div class="cart-drawer__empty-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M6 2L3 6V20C3 20.5304 3.21071 21.0391 3.58579 21.4142C3.96086 21.7893 4.46957 22 5 22H19C19.5304 22 20.0391 21.7893 20.4142 21.4142C20.7893 21.0391 21 20.5304 21 20V6L18 2H6Z"/><path d="M3 6H21"/><path d="M16 10C16 11.0609 15.5786 12.0783 14.8284 12.8284C14.0783 13.5786 13.0609 14 12 14C10.9391 14 9.92172 13.5786 9.17157 12.8284C8.42143 12.0783 8 11.0609 8 10"/></svg>
            </div>
            <p class="cart-drawer__empty-title">Your bag is empty</p>
            <p class="cart-drawer__empty-subtitle">Discover our Ayurvedic formulas targeted for female metabolism.</p>
            <a href="/collections/all" class="btn btn-primary btn-sm" data-cart-drawer-close>Explore Formulas</a>
          </div>
        `;
      } else {
        if (footer) footer.classList.remove('hidden');
        let html = '<ul class="cart-drawer__items-list">';
        cart.items.forEach(item => {
          html += `
            <li class="cart-drawer__item" data-line-item-key="${item.key}">
              <div class="cart-drawer__item-image">
                <img src="${item.featured_image ? item.featured_image.url : ''}" alt="${item.title}" width="72" height="84" loading="lazy">
              </div>
              <div class="cart-drawer__item-details">
                <div class="cart-drawer__item-header">
                  <h4 class="cart-drawer__item-title"><a href="${item.url}">${item.product_title}</a></h4>
                  <button type="button" class="cart-drawer__item-remove" data-remove-item="${item.key}" aria-label="Remove item">&times;</button>
                </div>
                ${item.variant_title ? `<p class="cart-drawer__item-variant">${item.variant_title}</p>` : ''}
                <div class="cart-drawer__item-bottom">
                  <div class="quantity-stepper" data-item-key="${item.key}">
                    <button type="button" class="quantity-stepper__btn" data-qty-change="-1" aria-label="Decrease">&minus;</button>
                    <span class="quantity-stepper__value" data-qty-val>${item.quantity}</span>
                    <button type="button" class="quantity-stepper__btn" data-qty-change="1" aria-label="Increase">&plus;</button>
                  </div>
                  <div class="cart-drawer__item-price">${formatMoney(item.final_line_price)}</div>
                </div>
              </div>
            </li>
          `;
        });
        html += '</ul>';
        itemsContainer.innerHTML = html;
      }
    }
  };

  // --------------------------------------------------------------------------
  // 2B. SEARCH DRAWER & LIVE PREDICTIVE SEARCH
  // --------------------------------------------------------------------------
  const SearchManager = {
    drawer: null,
    input: null,
    clearBtn: null,
    initialState: null,
    resultsContainer: null,
    isOpen: false,

    init() {
      this.drawer = document.getElementById('SearchDrawer');
      if (!this.drawer) return;

      this.input = this.drawer.querySelector('[data-search-input]');
      this.clearBtn = this.drawer.querySelector('[data-search-clear]');
      this.initialState = this.drawer.querySelector('[data-search-initial]');
      this.resultsContainer = this.drawer.querySelector('[data-search-results]');

      this.bindEvents();
    },

    bindEvents() {
      // Trigger Search Open (all buttons with [data-search-trigger])
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-search-trigger]');
        if (trigger) {
          e.preventDefault();
          this.open();
        }

        const closeBtn = e.target.closest('[data-search-close]');
        if (closeBtn) {
          e.preventDefault();
          this.close();
        }

        const tag = e.target.closest('[data-search-tag]');
        if (tag) {
          e.preventDefault();
          const query = tag.dataset.searchTag;
          if (this.input) {
            this.input.value = query;
            this.handleInput();
            this.input.focus();
          }
        }
      });

      // Clear button
      if (this.clearBtn) {
        this.clearBtn.addEventListener('click', () => {
          if (this.input) {
            this.input.value = '';
            this.input.focus();
            this.handleInput();
          }
        });
      }

      // Input typing with debounce
      if (this.input) {
        this.input.addEventListener('input', () => this.handleInput());
        this.input.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') {
            this.close();
          }
        });
      }

      // Close on ESC key globally
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) {
          this.close();
        }
      });
    },

    open() {
      const mobileNav = document.querySelector('[data-mobile-nav]');
      if (mobileNav && mobileNav.classList.contains('is-open')) {
        mobileNav.classList.remove('is-open');
      }
      this.drawer.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      this.isOpen = true;
      setTimeout(() => {
        if (this.input) this.input.focus();
      }, 150);
    },

    close() {
      this.drawer.classList.remove('is-open');
      document.body.style.overflow = '';
      this.isOpen = false;
    },

    handleInput() {
      const query = this.input ? this.input.value.trim() : '';

      if (this.clearBtn) {
        if (query.length > 0) {
          this.clearBtn.classList.remove('is-hidden');
        } else {
          this.clearBtn.classList.add('is-hidden');
        }
      }

      if (query.length < 2) {
        if (this.initialState) this.initialState.classList.remove('is-hidden');
        if (this.resultsContainer) {
          this.resultsContainer.classList.add('is-hidden');
          this.resultsContainer.innerHTML = '';
        }
        return;
      }

      if (this.initialState) this.initialState.classList.add('is-hidden');
      if (this.resultsContainer) this.resultsContainer.classList.remove('is-hidden');

      this.debouncedSearch(query);
    },

    debouncedSearch: debounce(function(query) {
      SearchManager.performSearch(query);
    }, 250),

    async performSearch(query) {
      if (!this.resultsContainer) return;
      this.resultsContainer.innerHTML = `
        <div class="search-drawer__loading" style="text-align:center; padding: 24px; color: #40614D;">
          Searching formulas and articles...
        </div>
      `;

      try {
        const url = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,article&resources[options][unavailable_products]=hide&resources[options][fields]=title,product_type,variants.title,vendor`;
        const res = await fetch(url);
        if (!res.ok) throw new Error('Search network error');
        const data = await res.json();
        this.renderResults(data, query);
      } catch (err) {
        console.error('Predictive search error:', err);
        this.renderFallback(query);
      }
    },

    renderResults(data, query) {
      const products = (data.resources && data.resources.results && data.resources.results.products) || [];
      const collections = (data.resources && data.resources.results && data.resources.results.collections) || [];
      const articles = (data.resources && data.resources.results && data.resources.results.articles) || [];

      if (products.length === 0 && collections.length === 0 && articles.length === 0) {
        this.resultsContainer.innerHTML = `
          <div class="search-drawer__empty">
            <h4 class="search-drawer__empty-title">No direct matches for "${query}"</h4>
            <p class="search-drawer__empty-desc">Try checking the spelling or browse our popular Ayurvedic formulas:</p>
            <div class="search-drawer__tags" style="justify-content: center;">
              <button type="button" class="search-drawer__tag" data-search-tag="SlimCore">SlimCore</button>
              <button type="button" class="search-drawer__tag" data-search-tag="ThyroSlim">ThyroSlim</button>
              <button type="button" class="search-drawer__tag" data-search-tag="PCOS">PCOS & PCOD</button>
              <button type="button" class="search-drawer__tag" data-search-tag="Metabolism">Metabolism</button>
            </div>
            <div class="search-drawer__view-all">
              <a href="/search?q=${encodeURIComponent(query)}" class="btn btn-primary search-drawer__view-all-btn">Search All Results</a>
            </div>
          </div>
        `;
        return;
      }

      let html = '<div class="search-drawer__results-inner">';

      if (collections.length > 0) {
        html += `<h4 class="search-drawer__section-title">Categories (${collections.length})</h4>`;
        html += '<div class="search-drawer__tags" style="margin-bottom: 18px;">';
        collections.forEach(c => {
          html += `<a href="${c.url}" class="search-drawer__tag" style="text-decoration:none; display:inline-flex; align-items:center; gap:6px;">🌿 ${c.title}</a>`;
        });
        html += '</div>';
      }

      if (products.length > 0) {
        html += `<h4 class="search-drawer__section-title">Formulas (${products.length})</h4>`;
        html += '<ul class="search-results-list">';
        products.forEach(p => {
          const price = p.price ? formatMoney(p.price * 100) : '';
          const imgUrl = p.featured_image ? p.featured_image.url : (p.image || '');
          html += `
            <li>
              <a href="${p.url}" class="search-result-item">
                ${imgUrl ? `<img src="${imgUrl}" alt="${p.title}" class="search-result-item__img" width="56" height="56" loading="lazy">` : ''}
                <div class="search-result-item__info">
                  <div class="search-result-item__title">${p.title}</div>
                  ${price ? `<div class="search-result-item__price">${price}</div>` : ''}
                </div>
                <span class="search-result-item__badge">Formula</span>
              </a>
            </li>
          `;
        });
        html += '</ul>';
      }

      if (articles.length > 0) {
        html += `<h4 class="search-drawer__section-title" style="margin-top:20px;">Journal Articles (${articles.length})</h4>`;
        html += '<ul class="search-results-list">';
        articles.forEach(a => {
          html += `
            <li>
              <a href="${a.url}" class="search-result-item">
                <div class="search-result-item__info">
                  <div class="search-result-item__title">${a.title}</div>
                  <div style="font-size:12px; color:rgba(46,42,36,0.6);">${a.summary ? a.summary.substring(0, 60) + '...' : 'Ayurvedic Science Note'}</div>
                </div>
                <span class="search-result-item__badge" style="background:#F0F5E0;">Article</span>
              </a>
            </li>
          `;
        });
        html += '</ul>';
      }

      html += `
        <div class="search-drawer__view-all">
          <a href="/search?q=${encodeURIComponent(query)}" class="btn btn-secondary search-drawer__view-all-btn">
            View all results for "${query}" &rarr;
          </a>
        </div>
      `;
      html += '</div>';

      this.resultsContainer.innerHTML = html;
    },

    renderFallback(query) {
      this.resultsContainer.innerHTML = `
        <div class="search-drawer__empty">
          <p class="search-drawer__empty-desc">Press search to see full catalog results:</p>
          <a href="/search?q=${encodeURIComponent(query)}" class="btn btn-primary search-drawer__view-all-btn">
            Search for "${query}"
          </a>
        </div>
      `;
    }
  };

  // --------------------------------------------------------------------------
  // 3. ACCORDIONS (Philosophy & FAQ)
  // --------------------------------------------------------------------------
  const AccordionManager = {
    init() {
      document.addEventListener('click', (e) => {
        const header = e.target.closest('.accordion-header');
        if (!header) return;

        const item = header.closest('.accordion-item');
        const group = item.closest('[data-accordion-group]');
        const isOpen = item.classList.contains('is-open');

        // Single active accordion within same group: opening 1 closes all others
        if (group) {
          group.querySelectorAll('.accordion-item').forEach(other => {
            if (other !== item) {
              other.classList.remove('is-open');
              const otherHeader = other.querySelector('.accordion-header');
              if (otherHeader) otherHeader.setAttribute('aria-expanded', 'false');
            }
          });
        }

        if (isOpen) {
          item.classList.remove('is-open');
          header.setAttribute('aria-expanded', 'false');
        } else {
          item.classList.add('is-open');
          header.setAttribute('aria-expanded', 'true');
        }
      });
    }
  };

  // --------------------------------------------------------------------------
  // 4. CATEGORY FILTER TABS
  // --------------------------------------------------------------------------
  const FilterTabsManager = {
    init() {
      document.addEventListener('click', (e) => {
        const tab = e.target.closest('.category-tab');
        if (!tab) return;

        const container = tab.closest('.featured-products-section');
        if (!container) return;

        container.querySelectorAll('.category-tab').forEach(t => t.classList.remove('active'));
        tab.classList.add('active');

        const filter = tab.dataset.filter;
        const items = container.querySelectorAll('.products-grid__item');

        items.forEach(item => {
          if (filter === 'all' || item.dataset.category === filter || item.dataset.category === 'all') {
            item.style.display = '';
          } else {
            item.style.display = 'none';
          }
        });
      });
    }
  };

  // --------------------------------------------------------------------------
  // 5. MOBILE NAVIGATION
  // --------------------------------------------------------------------------
  const MobileNavManager = {
    navEl: null,
    init() {
      this.navEl = document.querySelector('[data-mobile-nav]');
      if (!this.navEl) return;

      document.addEventListener('click', (e) => {
        if (e.target.closest('[data-mobile-menu-toggle]')) {
          this.navEl.classList.add('is-open');
          document.body.style.overflow = 'hidden';
        }
        if (e.target.closest('[data-mobile-menu-close]')) {
          this.navEl.classList.remove('is-open');
          document.body.style.overflow = '';
        }
      });
    }
  };

  // --------------------------------------------------------------------------
  // --------------------------------------------------------------------------
  // 6. COLLECTION FILTER MANAGER
  // --------------------------------------------------------------------------
  const CollectionFilterManager = {
    openModal() {
      const sidebar = document.getElementById('CollectionFilterSidebar') || document.querySelector('.collection-filter-sidebar');
      if (sidebar) {
        sidebar.classList.add('is-open');
        document.body.style.overflow = 'hidden';
      }
    },

    closeModal() {
      const sidebar = document.getElementById('CollectionFilterSidebar') || document.querySelector('.collection-filter-sidebar');
      if (sidebar) {
        sidebar.classList.remove('is-open');
        document.body.style.overflow = '';
      }
    },

    init() {
      // Open / Close Mobile Filter Modal
      document.addEventListener('click', (e) => {
        if (e.target.closest('[data-filter-modal-open]')) {
          e.preventDefault();
          this.openModal();
        }

        if (e.target.closest('[data-filter-modal-close]')) {
          e.preventDefault();
          this.closeModal();
        }
      });

      // Close on Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.closeModal();
        }
      });

      // Toggle Filter Groups
      document.addEventListener('click', (e) => {
        const header = e.target.closest('.filter-group__header');
        if (!header) return;

        const group = header.closest('.filter-group');
        if (!group) return;

        const isOpen = group.classList.contains('is-open');
        group.classList.toggle('is-open', !isOpen);
        header.setAttribute('aria-expanded', !isOpen);
      });

      // Handle "All formulas" checkbox mutual exclusivity
      document.addEventListener('change', (e) => {
        if (e.target.matches('input[name="concern"]')) {
          const allCheckbox = document.querySelector('input[name="concern"][value="all"]');
          const otherCheckboxes = Array.from(document.querySelectorAll('input[name="concern"]:not([value="all"])'));

          if (e.target === allCheckbox && allCheckbox.checked) {
            otherCheckboxes.forEach(cb => { cb.checked = false; });
          } else if (e.target !== allCheckbox && e.target.checked) {
            if (allCheckbox) allCheckbox.checked = false;
          } else {
            const anyChecked = otherCheckboxes.some(cb => cb.checked);
            if (!anyChecked && allCheckbox) {
              allCheckbox.checked = true;
            }
          }
        }
      });

      // Apply Filters Action
      document.addEventListener('click', (e) => {
        const applyBtn = e.target.closest('[data-apply-filters]');
        if (!applyBtn) return;

        applyBtn.style.transform = 'scale(0.97)';
        setTimeout(() => {
          applyBtn.style.transform = '';
        }, 150);

        // Filter products grid
        const selectedConcerns = Array.from(document.querySelectorAll('input[name="concern"]:checked')).map(cb => cb.value);
        const productItems = document.querySelectorAll('.collection-products-grid__item');

        productItems.forEach(item => {
          const itemConcerns = (item.dataset.concern || 'all').split(' ');
          if (selectedConcerns.includes('all') || selectedConcerns.length === 0) {
            item.style.display = '';
          } else {
            const hasMatch = selectedConcerns.some(c => itemConcerns.includes(c));
            item.style.display = hasMatch ? '' : 'none';
          }
        });

        // If on mobile, close the filter modal on apply
        if (window.innerWidth <= 768) {
          this.closeModal();
        }
      });
    }
  };

  // --------------------------------------------------------------------------
  // 7. PRODUCT CARD AUTO COLOR DETECTOR (Future-Proof Automatic Matching)
  // --------------------------------------------------------------------------
  const ProductCardColorManager = {
    init() {
      const cards = document.querySelectorAll('.product-card-wrapper');
      if (!cards.length) return;

      cards.forEach(card => {
        const img = card.querySelector('.product-card__image');
        if (!img) return;

        if (img.complete && img.naturalWidth > 0) {
          this.processImage(card, img);
        } else {
          img.addEventListener('load', () => this.processImage(card, img), { once: true });
        }
      });
    },

    processImage(card, img) {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = 30;
        canvas.height = 30;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) return;

        ctx.drawImage(img, 0, 0, 30, 30);
        const imgData = ctx.getImageData(0, 0, 30, 30).data;

        let totalR = 0, totalG = 0, totalB = 0, count = 0;

        for (let i = 0; i < imgData.length; i += 4) {
          const r = imgData[i];
          const g = imgData[i + 1];
          const b = imgData[i + 2];
          const a = imgData[i + 3];

          if (a < 128) continue; // Skip transparent
          const max = Math.max(r, g, b);
          const min = Math.min(r, g, b);
          const diff = max - min;
          const brightness = (r + g + b) / 3;

          if (brightness > 245 || brightness < 20) continue; // Skip pure white / black
          if (diff < 10 && brightness > 180) continue; // Greyish white

          totalR += r;
          totalG += g;
          totalB += b;
          count++;
        }

        if (count > 12) {
          const avgR = Math.round(totalR / count);
          const avgG = Math.round(totalG / count);
          const avgB = Math.round(totalB / count);

          const { h, s, l } = this.rgbToHsl(avgR, avgG, avgB);

          if (s > 0.08) {
            const hDeg = Math.round(h * 360);
            const satPct = Math.round(s * 100);

            // 1. Button: rich saturated tone
            const btnLightness = Math.min(Math.max(Math.round(l * 100), 28), 44);
            const btnSat = Math.max(satPct, 38);
            const btnColor = `hsl(${hDeg}, ${btnSat}%, ${btnLightness}%)`;
            const btnShadow = `hsla(${hDeg}, ${btnSat}%, ${btnLightness}%, 0.28)`;

            // 2. Card Background: soft pastel container tint (lightness ~93%)
            const cardSat = Math.min(Math.max(satPct, 22), 52);
            const cardBg = `hsl(${hDeg}, ${cardSat}%, 93%)`;

            card.style.setProperty('--card-bg', cardBg);
            card.style.setProperty('--btn-color', btnColor);
            card.style.setProperty('--btn-shadow', btnShadow);
          }
        }
      } catch (err) {
        // Silently preserve server-rendered Liquid color values if CORS restricts pixel sampling
      }
    },

    rgbToHsl(r, g, b) {
      r /= 255; g /= 255; b /= 255;
      const max = Math.max(r, g, b), min = Math.min(r, g, b);
      let h, s, l = (max + min) / 2;

      if (max === min) {
        h = s = 0;
      } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
          case r: h = (g - b) / d + (g < b ? 6 : 0); break;
          case g: h = (b - r) / d + 2; break;
          case b: h = (r - g) / d + 4; break;
        }
        h /= 6;
      }
      return { h, s, l };
    }
  };

  // --------------------------------------------------------------------------
  // INITIALIZE ON DOM READY
  // --------------------------------------------------------------------------
  document.addEventListener('DOMContentLoaded', () => {
    CartManager.init();
    SearchManager.init();
    AccordionManager.init();
    FilterTabsManager.init();
    MobileNavManager.init();
    CollectionFilterManager.init();
    ProductCardColorManager.init();
  });
})();
